import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ArrowLeft,
  Browsers,
  Copy,
  DotsThreeOutline,
  Fire,
  MagicWand,
  PaintBrushHousehold,
  Robot,
  Sparkle,
  Translate,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { cn } from "@stylingcv/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@stylingcv/ui";

import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { useDeleteResume } from "@/client/services/resume/delete";
import { useImportResume } from "@/client/services/resume/import";
import { BuilderLoadingOverlay } from "../_components/builder-loading-overlay";
import { CoverLetterGenerationModal } from "../_components/cover-letter-generation-modal";
import { TranslateResumeModal } from "../_components/translate-resume-modal";
import { LeftSidebar } from "../sidebars/left";
import { RightSidebar } from "../sidebars/right";
import {
  MOBILE_BOTTOM_NAV_OFFSET,
  MobileBottomNav,
  type MobileBuilderTab,
} from "../_components/mobile/bottom-nav";
import { MobileNavigation } from "../sidebars/left/components/mobile-navigation";
import { PreviewTab } from "../_components/mobile/preview-tab";

interface MobileLayoutProps {
  isArtboardLoading: boolean;
  onDownload: () => void;
}

export const MobileLayout = ({ isArtboardLoading, onDownload }: MobileLayoutProps) => {
  const [activeTab, setActiveTab] = useState<MobileBuilderTab>("sections");
  const [mobileEditSection, setMobileEditSection] = useState<string | null>(null);
  const [mobileAnalysisMode, setMobileAnalysisMode] = useState(false);
  const [toolsSheetOpen, setToolsSheetOpen] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);

  const resume = useResumeStore((state) => state.resume);
  const setActiveSectionId = useBuilderStore((state) => state.setActiveSectionId);
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const isRTL = i18n.locale.startsWith("ar");

  const { deleteResume } = useDeleteResume();
  const { importResume } = useImportResume();

  const handleSectionSelect = (sectionId: string) => {
    setActiveSectionId(sectionId);
    setMobileEditSection(sectionId);
    setMobileAnalysisMode(false);
  };

  const handleTabChange = (tab: MobileBuilderTab) => {
    setActiveTab(tab);
    if (tab !== "sections") {
      setMobileEditSection(null);
      setMobileAnalysisMode(false);
    }
    if (tab === "preview") {
      setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
    }
  };

  const handleBack = () => {
    if (mobileAnalysisMode) setMobileAnalysisMode(false);
    else setMobileEditSection(null);
  };

  const handleDuplicate = async () => {
    setToolsSheetOpen(false);
    await importResume({
      title: `${resume.title} (Copy)`,
      slug: `${resume.slug}-copy`,
      data: resume.data,
    });
  };

  const handleDelete = async () => {
    if (!resume.id) return;
    setToolsSheetOpen(false);
    if (!window.confirm(t`Are you sure you want to delete this resume? This action cannot be undone.`)) return;
    await deleteResume(resume.id);
    navigate("/dashboard/resumes");
  };

  const showTopBar = activeTab !== "preview";
  const hasBack = (mobileEditSection || mobileAnalysisMode) && activeTab === "sections";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#faf9f5]">
      <BuilderLoadingOverlay isLoading={isArtboardLoading} />
      <TranslateResumeModal isOpen={showTranslateModal} onClose={() => setShowTranslateModal(false)} />
      <CoverLetterGenerationModal isOpen={showCoverLetterModal} onClose={() => setShowCoverLetterModal(false)} onGenerated={() => {}} />

      {showTopBar && (
        <header className="flex shrink-0 items-center gap-2 border-b border-black/[0.07] bg-[#faf9f5] px-4 py-2.5">
          {hasBack ? (
            <button
              className="flex items-center gap-1 text-[#268599]"
              onClick={handleBack}
            >
              <ArrowLeft size={16} className={cn(isRTL && "rotate-180")} />
              <span className="text-[10px] font-semibold">{t`Sections`}</span>
            </button>
          ) : (
            <button
              className="p-1 text-[#268599]"
              onClick={() => navigate("/dashboard/resumes")}
              aria-label={t`Back`}
            >
              <ArrowLeft size={18} className={cn(isRTL && "rotate-180")} />
            </button>
          )}
          <h1 className="flex-1 truncate text-center text-[11px] font-bold text-[#0A1628]">
            {resume.title || t`Untitled Resume`}
          </h1>
          <button
            className="p-1 text-[#aaa]"
            onClick={() => setToolsSheetOpen(true)}
            aria-label={t`Resume tools`}
          >
            <DotsThreeOutline size={18} weight="fill" />
          </button>
        </header>
      )}

      <div
        className="min-h-0 flex-1 overflow-hidden"
        style={{ paddingBottom: MOBILE_BOTTOM_NAV_OFFSET }}
      >
        {/* Sections tab */}
        <div className={cn("flex h-full flex-col", activeTab !== "sections" && "hidden")}>
          {!mobileEditSection && !mobileAnalysisMode ? (
            <MobileNavigation
              onSectionSelect={handleSectionSelect}
              activeSectionId={mobileEditSection ?? undefined}
            />
          ) : mobileAnalysisMode ? (
            <div className="h-full overflow-hidden">
              <LeftSidebar activeTab="analysis" singleSectionMode />
            </div>
          ) : (
            <div className="h-full overflow-hidden">
              <LeftSidebar activeTab="fill-in" singleSectionMode />
            </div>
          )}
        </div>

        {/* Design tab */}
        <div className={cn("h-full", activeTab !== "design" && "hidden")}>
          <RightSidebar />
        </div>

        {/* Preview tab — always mounted so artboard stays live */}
        <div className={cn("flex h-full flex-col", activeTab !== "preview" && "hidden")}>
          <PreviewTab
            onDownload={onDownload}
            onSwitchToDesign={() => handleTabChange("design")}
          >
            <Outlet />
          </PreviewTab>
        </div>
      </div>

      <MobileBottomNav activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Resume Tools Sheet */}
      <Sheet open={toolsSheetOpen} onOpenChange={setToolsSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl border-t-0 p-0" style={{ maxHeight: "85vh", overflowY: "auto" }}>
          <SheetHeader className="border-b border-gray-100 px-4 pb-3 pt-4 text-left">
            <SheetTitle style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {t`Resume Tools`}
            </SheetTitle>
            <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{t`Actions on the whole document`}</p>
          </SheetHeader>

          <div style={{ padding: "8px 8px 40px" }}>
            <ResumeToolRow
              icon={<PaintBrushHousehold size={17} weight="duotone" />}
              label={t`Change resume design`}
              description={t`Colors, spacing & typography`}
              onClick={() => { setToolsSheetOpen(false); handleTabChange("design"); }}
            />
            <ResumeToolRow
              icon={<Sparkle size={17} weight="duotone" />}
              label={t`AI Suggestions`}
              description={t`Smart improvements for this resume`}
              badge="ai"
              onClick={() => {
                setToolsSheetOpen(false);
                setMobileAnalysisMode(true);
                setMobileEditSection(null);
                setActiveTab("sections");
              }}
            />
            <ResumeToolRow
              icon={<Browsers size={17} weight="duotone" />}
              label={t`Templates`}
              description={t`Browse all resume layouts`}
              badge="⌘T"
              onClick={() => { setToolsSheetOpen(false); handleTabChange("design"); }}
            />
            <ResumeToolRow
              icon={<Translate size={17} weight="duotone" />}
              label={t`Copy to other language`}
              description={t`Choose from 50+ languages`}
              badge="ai"
              onClick={() => { setToolsSheetOpen(false); setShowTranslateModal(true); }}
            />
            <ResumeToolRow
              icon={<Robot size={17} weight="duotone" />}
              label={t`ATS Check`}
              description={t`Score your resume for job boards`}
              badge="ai"
              onClick={() => {
                setToolsSheetOpen(false);
                setMobileAnalysisMode(true);
                setMobileEditSection(null);
                setActiveTab("sections");
              }}
            />
            <ResumeToolRow
              icon={<MagicWand size={17} weight="duotone" />}
              label={t`Generate cover letter`}
              description={t`Match this resume to a job`}
              badge="ai"
              onClick={() => { setToolsSheetOpen(false); setShowCoverLetterModal(true); }}
            />
            <ResumeToolRow
              icon={<Fire size={17} weight="duotone" />}
              label={t`Stylion — Resume Roast`}
              description={t`Get a royal AI critique of your resume`}
              badge="ai"
              onClick={() => { setToolsSheetOpen(false); navigate("/stylion"); }}
            />

            <div style={{ height: 1, background: "#f3f4f6", margin: "6px 4px" }} />

            <ResumeToolRow
              icon={<Copy size={17} weight="duotone" />}
              label={t`Duplicate`}
              description={t`Make a copy of this resume`}
              badge="⌘D"
              onClick={handleDuplicate}
            />
            <ResumeToolRow
              icon={<Trash size={17} weight="duotone" />}
              label={t`Delete resume`}
              description={t`Permanently remove this resume`}
              danger
              onClick={handleDelete}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Resume tool row — mirrors desktop BuilderTopbar dropdown item style
// ---------------------------------------------------------------------------
type ResumeToolRowProps = {
  icon: React.ReactNode;
  label: string;
  description: string;
  badge?: "ai" | string;
  danger?: boolean;
  onClick: () => void;
};

const ResumeToolRow = ({ icon, label, description, badge, danger, onClick }: ResumeToolRowProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "10px 8px",
        borderRadius: 12,
        border: "none",
        background: hovered ? (danger ? "rgba(220,38,38,0.05)" : "#f9fafb") : "transparent",
        cursor: "pointer",
        transition: "background 0.1s",
        textAlign: "left",
      }}
    >
      {/* Icon box */}
      <div style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: danger ? (hovered ? "rgba(220,38,38,0.08)" : "rgba(220,38,38,0.05)") : (hovered ? "#e5e7eb" : "#f3f4f6"),
        color: danger ? (hovered ? "#dc2626" : "#ef4444") : "#6b7280",
        transition: "background 0.1s, color 0.1s",
      }}>
        {icon}
      </div>

      {/* Label + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: 13, fontWeight: 600, lineHeight: 1.3,
          color: danger ? (hovered ? "#dc2626" : "#ef4444") : "#16344c",
          margin: 0,
        }}>
          {label}
        </p>
        <p style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {description}
        </p>
      </div>

      {/* Badge */}
      {badge === "ai" && (
        <span style={{
          flexShrink: 0, borderRadius: 6, border: "1px solid #fed7aa",
          background: "#fff7ed", padding: "2px 6px",
          fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" as const,
          color: "#f97316",
        }}>
          AI
        </span>
      )}
      {badge && badge !== "ai" && (
        <kbd style={{
          flexShrink: 0, borderRadius: 6, border: "1px solid #e5e7eb",
          background: "#f3f4f6", padding: "2px 6px",
          fontSize: 10, fontWeight: 500, color: "#9ca3af", fontFamily: "monospace",
        }}>
          {badge}
        </kbd>
      )}
    </button>
  );
};
